import { buildEditorialHubJsonLd } from "@/features/editorial-hub/editorial-hub-json-ld";
import { alternatesToLinks, buildLocalizedPageAlternates } from "@/i18n/alternates";
import { toAbsoluteUrl } from "@/lib/absolute-url";
import { buildPagePreviewMetaTags } from "@/lib/buildPagePreviewMetaTags";
import type { RouterOutputs } from "@/types/rpc";
import type { FeaturePageFamily } from "@garimpa/lib/featurePage";
import type { ViewerLocale } from "@garimpa/lib/viewerPreferences";

type FeaturePageLanding = RouterOutputs["featurePages"]["getLanding"];

export async function buildEditorialHubHead(input: {
  locale: ViewerLocale;
  title: string;
  description: string;
  landings: FeaturePageLanding[];
  families: Array<{
    family: FeaturePageFamily;
    title: string;
    description: string;
  }>;
}) {
  const alternates = buildLocalizedPageAlternates(input.locale, "editorial");
  const imageUrl = toAbsoluteUrl(
    input.landings.flatMap((landing) => landing.items).find((item) => item.image)?.image?.url,
  );
  const jsonLd = buildEditorialHubJsonLd({
    description: input.description,
    families: input.families,
    locale: input.locale,
    title: input.title,
    url: alternates.canonical,
  });

  return {
    meta: await buildPagePreviewMetaTags(
      {
        title: input.title,
        description: input.description,
        canonicalUrl: alternates.canonical,
        imageUrl,
      },
      {
        robots: "index,follow",
        openGraph: { type: "website", image: { alt: input.title } },
        twitter: {},
      },
      { locale: input.locale },
    ),
    links: alternatesToLinks(alternates),
    scripts: [{ type: "application/ld+json", children: JSON.stringify(jsonLd) }],
  };
}
